import React from 'react';
import PageHero from './PageHero';
import { Trophy, Users } from 'lucide-react';
import heroImg from '../assets/PRIMARY SCHOOL.png';
import studentsImg from '../assets/Banner-GMIS Students.png';

export default function HouseSystemPage({ setPage, t }) {
  const navCategory = t?.nav?.[3]?.label || "School Life";
  const homeLabel = t?.nav?.[0]?.label || "Home";
  const houses = [
    {
      name: 'Kartini House',
      motto: 'Through Darkness into Light',
      dob: '21st April 1879',
      leader: 'Raden Adjeng Kartini',
      accentColor: 'border-amber-400',
      badgeBg: 'bg-amber-500/10 text-amber-700',
      cardHover: 'hover:border-amber-400',
      desc: "Raden Adjeng Kartini (D.O.B. 21st April 1879) was a Javanese noblewoman and is best known as a pioneer in the area of women's rights for native Indonesians. She protested against the practice of polygamy and against the Dutch suppression of the island's native population."
    },
    {
      name: 'Lincoln House',
      motto: 'Strive, Seek & Succeed',
      dob: '12th February 1809',
      leader: 'Abraham Lincoln',
      accentColor: 'border-blue-500',
      badgeBg: 'bg-blue-500/10 text-blue-700',
      cardHover: 'hover:border-blue-500',
      desc: "Abraham Lincoln (D.O.B. 12th February 1809), the 16th president of the United States, guided his country through the most devastating experience in its national history—the CIVIL WAR. He is considered by many historians to have been the greatest American President."
    },
    {
      name: 'Tagore House',
      motto: 'Truth Conquers All',
      dob: '7th May 1861',
      leader: 'Rabindranath Tagore',
      accentColor: 'border-emerald-500',
      badgeBg: 'bg-emerald-500/10 text-emerald-700',
      cardHover: 'hover:border-emerald-500',
      desc: "Rabindranath Tagore (D.O.B. 7th May 1861), the Nobel laureate poet, writer, philosopher, social reformer, patriot and, above all, a great humanitarian and philosopher, was the ambassador of Indian culture to the rest of the world. His multifaceted talent showered upon different branches of art, such as novels, short stories, dramas, articles, essays, painting, etc., and his songs, popularly known as Rabindrasangeet, have an eternal appeal. The national anthems of India and Bangladesh are taken from his composition."
    },
    {
      name: 'Tolstoy House',
      motto: 'The Ability to Be',
      dob: '9th September 1828',
      leader: 'Lev Nikolayevitch Tolstoy',
      accentColor: 'border-purple-500',
      badgeBg: 'bg-purple-500/10 text-purple-700',
      cardHover: 'hover:border-purple-500',
      desc: "Lev Nikolayevitch Tolstoy (D.O.B. 9th September 1828) was a Russian writer, philosopher and social activist credited as a major influence on Christian anarchism. Tolstoy's major work, War and Peace, came out between the years 1865 and 1869. This epic tale reflected Tolstoy's view that all is predestined, but we cannot live unless we imagine that we have free will."
    }
  ];

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title="House System"
        eyebrow={navCategory}
        subtitle="Promoting healthy competition, teamwork, and school spirit."
        breadcrumbs={[homeLabel, navCategory, "House System"]}
        bgImage={heroImg}
      />

      {/* ── Main Content Container ────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-[80px] py-16 lg:py-24 box-border">

        {/* ── Section 1: Introduction & Overview ── */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-20 lg:mb-24">
          <div className="w-full lg:w-5/12">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/15 rounded-2xl -z-10" />
              <img
                src={studentsImg}
                alt="House System at GMIS Jakarta"
                className="relative z-10 w-full h-[360px] md:h-[420px] rounded-2xl shadow-xl object-cover border-4 border-white"
              />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-navy/10 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="w-full lg:w-7/12 flex flex-col gap-6 text-left">
            <div>
              <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">{navCategory}</span>
              </div>
              <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight uppercase">
                House System
              </h2>
              <div className="w-16 h-1 bg-gold mt-4" />
            </div>

            <p className="text-[17px] md:text-[19px] font-medium text-navy leading-relaxed">
              The House System is an integral part of life at GMIS and aims to promote a spirit of healthy competition, teamwork, and school spirit through a wide range of academic, cultural, and sports-based activities.
            </p>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0 mt-0.5">
                  <Users className="w-5 h-5 text-gold-ink" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  All students and staff members are assigned to one of four houses, each named after inspirational historical leaders. These houses foster leadership, collaboration, and a strong sense of belonging within the school community.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-line shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0 mt-0.5">
                  <Trophy className="w-5 h-5 text-navy" />
                </div>
                <p className="text-[16px] text-ink/80 leading-relaxed m-0">
                  Throughout the year, inter-house competitions are held in various areas including sports, quizzes, debates, arts, and community service. Points are awarded for performance, participation, and conduct, contributing to the overall House Championship at the end of the academic year.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2: The Four Houses ── */}
        <div className="mb-20 lg:mb-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold block mb-2">
              Four Inspirational Leaders
            </span>
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-navy uppercase">
              Our Four Houses
            </h3>
            <div className="w-12 h-1 bg-gold mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {houses.map((house, idx) => (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-7 lg:p-8 border border-line shadow-sm ${house.cardHover} transition-all duration-300 flex flex-col justify-between`}
              >
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h4 className="text-2xl font-heading font-bold text-navy">
                        {house.name}
                      </h4>
                      <p className="text-gold font-medium text-[15px] mt-0.5 italic">
                        “{house.motto}”
                      </p>
                    </div>
                    <span className={`text-[12px] font-semibold px-3 py-1 rounded-full shrink-0 ${house.badgeBg}`}>
                      D.O.B. {house.dob}
                    </span>
                  </div>

                  <p className="text-[15px] text-ink/80 leading-relaxed pt-2 border-t border-line/60">
                    {house.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 3: Concluding Summary Banner ── */}
        <div className="w-full bg-navy rounded-3xl p-10 md:p-14 text-center text-white shadow-xl relative overflow-hidden">
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <h3 className="font-heading font-bold text-xl sm:text-2xl md:text-3xl leading-snug max-w-3xl mx-auto tracking-tight mb-8">
            “Being part of a house gives students opportunities to grow as individuals and as team players, building confidence, discipline, and school pride.”
          </h3>
          <div className="flex justify-center">
            <button
              onClick={() => {
                if (setPage) setPage('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-3.5 rounded-lg bg-gold text-navy font-heading font-bold text-xs uppercase tracking-widest hover:bg-gold/90 transition-all shadow-md"
            >
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
